/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Table as AntTable, Avatar, Button, Col } from "antd";
import { notify } from "@/shared/utils/notify";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { popularProductsService } from "../../../shared/services";
import { PATH_S3_BUCKET } from "../../../shared/utils/constants";
import { LucideIcon } from "../../../shared/components/ui/icons/LucideIcon";
import { TableColumns } from "../../../shared/components/ui/Table/types";
import { ModalAddPopularProduct } from "./ModalAddPopularProduct";
import { usePopularProducts } from "../hooks/usePopularProducts";

function createDraggableWrapper(rowKeys: string[]) {
  return function DraggableWrapper(props: any) {
    const { children, ...restProps } = props;
    return (
      <SortableContext items={rowKeys} strategy={verticalListSortingStrategy}>
        <tbody {...restProps} style={{ ...restProps.style, pointerEvents: "auto" }}>
          {children}
        </tbody>
      </SortableContext>
    );
  };
}

function DraggableRow(props: any) {
  const rowKey = props["data-row-key"] ?? props.record?.id ?? props.index;
  const id = String(rowKey);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const { children, ...restProps } = props;
  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging
      ? { zIndex: 999, background: "#fafafa", boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }
      : {}),
  };
  // Solo el primer cell (handle) tiene listeners; evita que el clic en eliminar inicie drag
  const cells = React.Children.map(children, (child, index) => {
    if (index === 0 && React.isValidElement(child)) {
      return React.cloneElement(child as React.ReactElement<any>, {
        ...attributes,
        ...listeners,
        style: {
          ...(child.props?.style ?? {}),
          cursor: isDragging ? "grabbing" : "grab",
          touchAction: "none",
        },
      });
    }
    return child;
  });
  return (
    <tr ref={setNodeRef} {...restProps} style={style}>
      {cells}
    </tr>
  );
}

interface ProductRow {
  id: number; // popular_products.id (para remove/patch)
  product_id: number;
  name: string;
  position_more_sales: number;
  media?: { path: string; priority: number }[];
}

export const TablePopularProducts = () => {
  const { addProduct, removeProduct, updatePositions, loading } = usePopularProducts();
  const [data, setData] = useState<ProductRow[]>([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setTableLoading(true);
    try {
      const res = await popularProductsService.find({
        query: { $limit: 500, $sort: { position: 1 } },
      });
      const items = (res as any)?.data ?? res ?? [];
      setData(Array.isArray(items) ? items : []);
    } catch (err) {
      notify.error("Error al cargar productos populares");
    } finally {
      setTableLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = data.findIndex((item) => String(item.id) === active.id);
    const newIndex = data.findIndex((item) => String(item.id) === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(data, oldIndex, newIndex);
    setData(reordered);

    const updates = reordered.map((item, index) => ({
      id: item.product_id ?? item.id,
      position_more_sales: index + 1,
    }));
    const ok = await updatePositions(updates);
    if (!ok) fetchData();
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const columns: TableColumns<ProductRow> = [
    {
      key: "dragHandle",
      dataIndex: "dragHandle",
      title: "",
      width: 48,
      render: () => (
        <span
          title="Arrastra para reordenar"
          style={{ color: "#999", display: "inline-flex", padding: "4px" }}
        >
          <GripVertical size={20} />
        </span>
      ),
    },
    {
      title: "Pos.",
      width: 5,
      dataIndex: "position_more_sales",
      key: "position_more_sales",
    },
    {
      title: "Producto",
      width: 20,
      key: "product",
      render: (_, item) => (
        <Col style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar
            size={48}
            shape="square"
            src={
              item.media?.[0]
                ? `${PATH_S3_BUCKET}/${item.media.sort((a: any, b: any) => b.priority - a.priority)[0]?.path}`
                : null
            }
            style={{
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              backgroundColor: "#f7fafc",
              color: "#718096",
            }}
          >
            {!item.media?.[0] && "Sin foto"}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500 }}>{item.name}</div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>#{item.product_id ?? item.id}</div>
          </div>
        </Col>
      ),
    },
    {
      title: "Acciones",
      key: "operation",
      width: 10,
      render: (_, item) => (
        <Col style={{ display: "flex", gap: 8 }}>
          <Button
            type="text"
            danger
            onPointerDown={(e) => e.stopPropagation()}
            onClick={async (e) => {
              e.stopPropagation();
              const productId = item.product_id ?? item.id;
              const ok = await removeProduct(productId);
              if (ok) {
                setData((prev) => prev.filter((p) => (p.product_id ?? p.id) !== productId));
                fetchData();
              }
            }}
            disabled={loading}
            style={{ borderRadius: "8px", padding: "8px" }}
          >
            <LucideIcon>
              <Trash2 size={18} />
            </LucideIcon>
          </Button>
        </Col>
      ),
    },
  ];

  const bodyWrapper = useMemo(
    () => createDraggableWrapper(data.map((d) => String(d.id))),
    [data]
  );

  const activeItem = activeId ? data.find((d) => String(d.id) === activeId) : null;

  return (
    <>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}>
        <Button
          type="primary"
          icon={
            <LucideIcon>
              <Plus size={18} />
            </LucideIcon>
          }
          onClick={() => setModalOpen(true)}
          loading={loading}
          style={{ borderRadius: "8px", height: 40, fontWeight: 500 }}
        >
          Agregar producto
        </Button>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div style={{ margin: "15px 0", width: "100%", alignSelf: "stretch" }}>
          <p style={{ marginBottom: 8, color: "#666", fontSize: 13 }}>
            Arrastra una fila para cambiar el orden. Estos productos aparecerán en la sección
            &quot;Populares&quot; de la app.
          </p>
          <AntTable<ProductRow>
            className="tableStyleOne modern-pagination"
            style={{ width: "100%" }}
            columns={columns}
            dataSource={data}
            loading={tableLoading}
            rowKey={(record) => String(record.id)}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (t) => `${t} productos`,
              locale: { items_per_page: "por página" },
            }}
            components={{
              body: {
                wrapper: bodyWrapper,
                row: DraggableRow,
              },
            }}
          />
        </div>
        <DragOverlay>
          {activeItem ? (
            <div
              style={{
                background: "#fff",
                padding: "12px 16px",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                cursor: "grabbing",
                border: "1px solid #e8e8e8",
              }}
            >
              {activeItem.name} (#{activeItem.id})
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <ModalAddPopularProduct
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAdded={fetchData}
        excludeIds={data.map((d) => d.product_id ?? d.id)}
        nextPosition={data.length + 1}
        addProduct={addProduct}
        loading={loading}
      />
    </>
  );
};
